import React, { useState, useEffect, useRef } from 'react';
import { useAPI } from '../../../Hooks/useAPIs';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import UserAutocomplete from '../../../Hooks/useUserAutocomplete';
import { sanitizeMacAddress } from '../../../utils/macAddress';
import { DEVICE_TYPE_OPTIONS } from '../../../utils/deviceTypes';

const CreateDeviceDialog = ({
  open,
  onClose,
  createForm,
  setCreateForm,
  createError,
  createDeviceMutation,
  handleCreate,
  REGIONS,
}) => {
  const [macError, setMacError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [verificationMessage, setVerificationMessage] = useState('');
  const { apiCall } = useAPI();
  const lastVerifiedMacRef = useRef(null);
  const isVerifyingRef = useRef(false);

  const handleMacChange = (e) => {
    const sanitizedValue = sanitizeMacAddress(e.target.value);
    setCreateForm({ ...createForm, mac_addr: sanitizedValue });
    
    if (lastVerifiedMacRef.current !== sanitizedValue) {
      setVerificationStatus(null);
      setVerificationMessage('');
      lastVerifiedMacRef.current = null;
    }
    
    if (sanitizedValue.length < 12) {
      setMacError('MAC must be at least 12 characters.');
    } else {
      setMacError('');
    }
  };

  const handleVerifyMac = async () => {
    const macToVerify = sanitizeMacAddress(createForm.mac_addr);
    
    if (!macToVerify || macToVerify.length < 12 || !/^[A-Za-z0-9]{12,}$/.test(macToVerify)) {
      setMacError('MAC must be at least 12 alphanumeric characters.');
      return;
    }

    if (lastVerifiedMacRef.current === macToVerify || isVerifyingRef.current) {
      return;
    }

    isVerifyingRef.current = true;
    setVerificationStatus('checking');
    setVerificationMessage('Verifying MAC address...');
    setMacError('');

    try {
      const response = await apiCall('POST', 'api/v1/devices/check-ownership', {
        mac_addresses: [macToVerify]
      });

      const results = response.data;
      const devicesWithOwners = results?.devices_with_owners || [];
      const availableDevices = results?.available_devices || [];
      const newDevices = results?.new_devices || [];

      if (devicesWithOwners.length > 0) {
        const device = devicesWithOwners[0];
        const owner = device.user;
        const ownerName = owner?.display_name || owner?.email || 'Unknown';
        setVerificationStatus('owned');
        setVerificationMessage(`This device is already owned by ${ownerName}.`);
        lastVerifiedMacRef.current = macToVerify;
      } else if (availableDevices.includes(macToVerify)) {
        setVerificationStatus('exists');
        setVerificationMessage('This MAC address already exists in the system. Cannot create duplicate device.');
        lastVerifiedMacRef.current = macToVerify;
      } else if (newDevices.some(d => d.mac_addr === macToVerify)) {
        setVerificationStatus('verified');
        setVerificationMessage('MAC address is available for creation.');
        lastVerifiedMacRef.current = macToVerify;
      } else {
        setVerificationStatus('verified');
        setVerificationMessage('MAC address is available for creation.');
        lastVerifiedMacRef.current = macToVerify;
      }
    } catch (error) {
      setVerificationStatus('error');
      setVerificationMessage('Could not verify MAC address. Please proceed with caution.');
      lastVerifiedMacRef.current = macToVerify;
    } finally {
      isVerifyingRef.current = false;
    }
  };

  useEffect(() => {
    if (!open) {
      setVerificationStatus(null);
      setVerificationMessage('');
      lastVerifiedMacRef.current = null;
      isVerifyingRef.current = false;
    }
  }, [open]);

  return (
    <Box>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Device</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          {macError && <Alert severity="error" sx={{ mb: 2 }}>{macError}</Alert>}
          
          {verificationStatus === 'checking' && (
            <Alert 
              severity="info" 
              icon={<CircularProgress size={16} />}
              sx={{ mb: 2 }}
            >
              {verificationMessage}
            </Alert>
          )}
          
          {verificationStatus === 'verified' && (
            <Alert 
              severity="success" 
              icon={<CheckCircleIcon />}
              sx={{ mb: 2 }}
            >
              {verificationMessage}
            </Alert>
          )}
          
          {verificationStatus === 'owned' && (
            <Alert 
              severity="error" 
              icon={<ErrorIcon />}
              sx={{ mb: 2 }}
            >
              {verificationMessage}
            </Alert>
          )}
          
          {verificationStatus === 'exists' && (
            <Alert 
              severity="warning" 
              icon={<ErrorIcon />}
              sx={{ mb: 2 }}
            >
              {verificationMessage}
            </Alert>
          )}
          
          {verificationStatus === 'error' && (
            <Alert 
              severity="warning" 
              icon={<ErrorIcon />}
              sx={{ mb: 2 }}
            >
              {verificationMessage}
            </Alert>
          )}

          <Box sx={{ mb: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                label="MAC Address"
                fullWidth
                value={createForm.mac_addr}
                onChange={handleMacChange}
                placeholder="Enter MAC"
                disabled={createDeviceMutation.isLoading || verificationStatus === 'checking'}
                error={verificationStatus === 'owned' || verificationStatus === 'exists'}
                helperText={
                  verificationStatus === 'checking' 
                    ? 'Verifying...' 
                    : verificationStatus === 'verified' 
                      ? 'MAC address is available' 
                      : verificationStatus === 'owned' || verificationStatus === 'exists'
                        ? 'This MAC address cannot be used'
                        : createForm.mac_addr.length >= 12
                          ? 'Click Verify to check availability'
                          : 'Special characters are removed automatically. Enter at least 12 characters'
                }
              />
              {createForm.mac_addr.length >= 12 && (
                <Button
                  variant="outlined"
                  onClick={handleVerifyMac}
                  disabled={
                    createDeviceMutation.isLoading ||
                    verificationStatus === 'checking' ||
                    lastVerifiedMacRef.current === createForm.mac_addr.trim()
                  }
                  sx={{ mt: 0.5, minWidth: '100px' }}
                  startIcon={verificationStatus === 'checking' ? <CircularProgress size={16} /> : null}
                >
                  {verificationStatus === 'checking' ? 'Verifying...' : 'Verify'}
                </Button>
              )}
            </Box>
          </Box>

          <FormControl 
            fullWidth 
            disabled={
              createDeviceMutation.isLoading || 
              verificationStatus === 'checking' || 
              (verificationStatus !== 'verified' && verificationStatus !== 'error')
            } 
            sx={{ mb: 2 }}
          >
            <InputLabel>Region</InputLabel>
            <Select
              value={createForm.region_code}
              label="Region"
              onChange={(e) =>
                setCreateForm({ ...createForm, region_code: e.target.value })
              }
            >
              {REGIONS.map((r) => (
                <MenuItem key={r.code} value={r.code}>
                  {r.code} - {r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl 
            fullWidth 
            disabled={
              createDeviceMutation.isLoading || 
              verificationStatus === 'checking' || 
              (verificationStatus !== 'verified' && verificationStatus !== 'error')
            } 
            sx={{ mb: 2 }}
          >
            <InputLabel>Device Type</InputLabel>
            <Select
              value={createForm.device_type || ''}
              label="Device Type"
              onChange={(e) =>
                setCreateForm({ ...createForm, device_type: e.target.value })
              }
            >
              <MenuItem value="">None (Optional)</MenuItem>
              {DEVICE_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <UserAutocomplete
            value={selectedUser}
            onChange={(user) => {
              setSelectedUser(user);
              setCreateForm((prev) => ({ ...prev, user_id: user?.id ?? null }));
            }}
            disabled={
              createDeviceMutation.isLoading || 
              verificationStatus === 'checking' || 
              (verificationStatus !== 'verified' && verificationStatus !== 'error')
            }
            label="Assign to User"
            placeholder="Search user..."
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={createDeviceMutation.isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={
              createDeviceMutation.isLoading ||
              !createForm.mac_addr ||
              !createForm.region_code ||
              macError !== '' ||
              verificationStatus === 'checking' ||
              verificationStatus === 'owned' ||
              verificationStatus === 'exists' ||
              (verificationStatus !== 'verified' && verificationStatus !== 'error' && createForm.mac_addr.length >= 12)
            }
            startIcon={createDeviceMutation.isLoading ? <CircularProgress size={16} /> : null}
          >
            {verificationStatus === 'checking' ? 'Verifying...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateDeviceDialog;